import { Component, inject, Input, OnInit, model, effect } from '@angular/core'
import { ChartOptions } from 'src/app/shared/models/chart-options'
import { PricingManagerClient, VCProductSearchResult } from 'src/app/shared/services/ppv.api'
import { VetcorPricingRowService } from '../vetcor-pricing-row.service'
import { PricingCalculator } from '../../pricing-calculator'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { Subject } from 'rxjs'
import { AuthManager } from 'src/app/shared/auth/AuthManager'

@Component({
   selector: 'vetcor-pricing-details-modal',
   templateUrl: './vetcor-pricing-details-modal.component.html',
   styleUrls: ['./vetcor-pricing-details-modal.component.scss'],
   standalone: false,
})
export class VetcorPricingDetailsModal implements OnInit {
   userService = inject(AuthManager)

   @Input() isCaptain: boolean = false
   @Input() isPracticeChange: boolean = false

   selectedChartYear: number = new Date().getFullYear()
   chartYears = [new Date().getFullYear(), new Date().getFullYear() - 1]
   priceChangeData: any[] = []
   recOverrideReason: string = ''

   product = this.pricingRowService.selectedPrice
   group = this.pricingRowService.selectedGroup

   public vcProduct = model<VCProductSearchResult>(undefined)

   constructor(
      private bsModalRef: BsModalRef,
      public pricingRowService: VetcorPricingRowService,
      private pricingManagerClient: PricingManagerClient
   ) {
      effect(() => {
         if (!this.product() || !this.pricingRowService.prices()?.length) return

         const match = this.pricingRowService.prices().find((p) => p.code === this.product().code)
         if (!match) return

         this.getCompetitorPrice(match)
      })
   }

   ngOnInit(): void {
      console.log(this.group())
   }

   onYearChange(pimsId: string, practiceSKey: number): void {
      const practiceSKeyNumber = Number(practiceSKey)
      this.pricingRowService.getTransactionHistory(practiceSKeyNumber, pimsId)
   }

   getFormattedPct(value: number): string {
      return (value * 100).toFixed(2) + '%'
   }

   scrollDown(): void {
      const element = this.isPracticeChange ? document.getElementById('priceChangeHistory') : document.getElementById('historicalTrendData')
      if (element) {
         element.scrollIntoView({ behavior: 'smooth' })
      }
   }

   onModifyPriceClick() {
      this.pricingRowService.updateRowVMs([{ ...this.product(), zoho: undefined }])
      this.pricingRowService.savePricingOps(this.product())
   }

   openTicket(productForDetails) {
      window.open('https://servicedesk.vetcor.com/agent/vetcorllc/pricing/tickets/details/' + productForDetails.zohoTicketId, '_blank')
   }

   onBeginDiscussionClick() {
      this.pricingRowService.updateRowVMs([{ ...this.product(), zoho: true }])
      this.pricingRowService.savePricingOps(this.product())
   }

   toggleEdit(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }
      product.isEditing = !product.isEditing
      product.zoho = undefined
   }

   onPriceReject(product: any) {
      this.pricingRowService.reject(product, product.finalComment)
   }

   onPriceApprove(product: any) {
      this.pricingRowService.approve(product)
   }

   getOverrideRec(product) {
      const reviewPrice = product.reviewPrice

      if (!!product.reviewPrice && product.reviewPrice !== 0) {
         return 'N/A'
      }

      if (reviewPrice <= product.lowballPriceFloor) {
         return 'Reject Override'
      } else if (reviewPrice > product.lowballPriceFloor) {
         if (reviewPrice > product.modelPrice) {
            return 'Approve Override'
         } else if (reviewPrice < product.modelPrice && reviewPrice >= 1.05 * product.averageUnitRetail) {
            return 'Approve Override'
         }
      } else if (reviewPrice < product.modelPrice && reviewPrice < 1.05 * product.averageUnitRetail) {
         return 'Reject Override'
      } else {
         return 'N/A'
      }
   }

   cancelEdit(product: any, event?: Event): void {
      if (event) {
         event.stopPropagation()
      }

      const editablePriceField = this.isCaptain ? 'opsPrice' : 'newPrice'
      const editableRationaleField = this.isCaptain ? 'finalComment' : 'rationale'
      product[editablePriceField] = null
      product[editableRationaleField] = ''
      this.toggleEdit(product)
   }

   onCancelClicked() {
      if (this.product().approvalStatus === 'Approved' || this.product().approvalStatus === 'Rejected' || this.product().isEditing) {
         this.toggleEdit(this.product())
      } else this.cancelEdit(this.product())
   }

   onPriceSaveRemoveOps(product: any) {
      this.pricingRowService.undoCaptainPricingChangeRequest(product).subscribe({
         error: (err) => console.error('ERROR: failed to save remove ops', err),
      })
   }

   onPriceSaveOps(product: any) {
      this.pricingRowService.savePricingOps(product)
   }

   getPriceImpact(group: any, forOps: boolean): number | null {
      if (!group.product) {
         return null
      }
      return PricingCalculator.getPriceImpact(group, forOps)
   }

   getPriceImpactPct(group: any, forOps: boolean): number | null {
      if (!group.product) {
         return null
      }
      const requestedImpact = this.getPriceImpact(group, forOps)
      return Math.round(((requestedImpact * 100) / group.product.l12MRevenue) * 100) / 100.0
   }

   hasPriceImpact(group: any, forOps: boolean): boolean {
      return PricingCalculator.doesProductGroupHavePriceImpact(group, forOps)
   }

   // Validation and helper methods
   isValidRationale(product: any, forOps: boolean): boolean {
      if (forOps) {
         return !(product.opsPrice && (!product.finalComment || !product.finalComment.trim()))
      } else {
         return !(product.newPrice && (!product.rationale || !product.rationale.trim()))
      }
   }

   getCompetitorPrice(priceRow: any) {
      this.pricingManagerClient.searchVCProducts(undefined, priceRow.practiceSKey, priceRow.vcProductSKey, undefined).subscribe({
         next: (data) => {
            if (data?.length) {
               this.vcProduct.set(data[0])
            } else {
               this.vcProduct.set(undefined)
            }
         },
         error: (err) => {
            console.error('Failed to get competitor price:', err)
            this.vcProduct.set(undefined)
         },
      })
   }

   isStaleDate(date: string | Date | null | undefined): boolean {
      if (!date) return false
      const scrapeTime = new Date(date).getTime()
      const nowMinus3Days = new Date().getTime() - 3 * 86400000
      return scrapeTime < nowMinus3Days
   }

   onInputClick(event: Event): void {
      event.stopPropagation()
   }

   onClose(): void {
      this.bsModalRef?.hide()
   }
}
